import React from 'react'
import {
  Box,
  Chip,
  Select,
  MenuItem,
  Checkbox,
  TextField,
  InputLabel,
  FormControl,
  ListItemText,
  Autocomplete,
  OutlinedInput,
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'

import { TProcedureFilters } from 'lib/hooks/useFilters'
import { TProcedureFiltersModalProps } from './ProcedureFilters.types'
import { useProcedureFiltersModalLogic } from './useProcedureFiltersModalLogic'
import { CustomModal } from 'components/molecules/Modal/Modal'
import './ProcedureFiltersModal.scss'

const ProcedureFiltersModal: React.FC<TProcedureFiltersModalProps> = ({
  open,
  onClose,
  filters,
  onApplyFilters,
  onClearFilters,
  surgeries = [],
}) => {
  const {
    MenuProps,
    roomOptions,
    localFilters,
    surgeonOptions,
    locationOptions,
    hospitalOptions,
    surgeryNameOptions,
    handleApply,
    handleClear,
    setLocalFilters,
    handleArrayFilterChange,
  } = useProcedureFiltersModalLogic(
    open,
    filters,
    surgeries,
    onApplyFilters,
    onClearFilters,
    onClose
  )

  return (
    <CustomModal
      open={open}
      header="Filter Procedures"
      primaryButtonText="Apply Filters"
      secondaryButtonText="Clear All"
      secondaryButtonAction={handleClear}
      handleOnSubmit={handleApply}
      icon={<FilterListIcon />}
      onCloseTakeSecondaryButtonAction={true}
      isLoadingState={false}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleApply()
        }}
        className="procedure-filters-modal-form"
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <FormControl fullWidth>
            <InputLabel id="hospital-filter-label">Hospitals</InputLabel>
            <Select
              labelId="hospital-filter-label"
              id="hospital-filter"
              multiple
              value={localFilters.rooms}
              onChange={(e) => handleArrayFilterChange(e, 'rooms')}
              input={<OutlinedInput label="Hospitals" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {hospitalOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    checked={localFilters.rooms.indexOf(option.value) > -1}
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            id="surgeon-filter"
            options={surgeonOptions}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.label
            }
            value={
              localFilters.surgeonName
                ? {
                    value: localFilters.surgeonName,
                    label: localFilters.surgeonName,
                  }
                : null
            }
            onChange={(_, newValue) => {
              setLocalFilters((prev: TProcedureFilters) => ({
                ...prev,
                surgeonName: newValue ? newValue.value : null,
              }))
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Surgeon Name"
                variant="outlined"
                placeholder="Select a surgeon"
              />
            )}
            isOptionEqualToValue={(option, value) => {
              if (!value) return false
              return typeof value === 'string'
                ? option.value === value
                : option.value === value.value
            }}
          />

          <Autocomplete
            id="procedure-filter"
            options={surgeryNameOptions}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.label
            }
            value={
              localFilters.surgeryName
                ? {
                    value: localFilters.surgeryName,
                    label: localFilters.surgeryName,
                  }
                : null
            }
            onChange={(_, newValue) => {
              setLocalFilters((prev: TProcedureFilters) => ({
                ...prev,
                surgeryName: newValue ? newValue.value : null,
              }))
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Surgery"
                variant="outlined"
                placeholder="Select a surgery"
              />
            )}
            isOptionEqualToValue={(option, value) => {
              if (!value) return false
              return typeof value === 'string'
                ? option.value === value
                : option.value === value.value
            }}
          />
        </Box>
      </form>
    </CustomModal>
  )
}

export default ProcedureFiltersModal
